/**
 * Maintenance / announcement banner selection.
 *
 * Pure and Convex-free on purpose: the same three decisions — is this banner
 * live right now, does it target the page I am on, which language do I read it
 * in — are taken by the admin table (to draw a status chip) and by the bar
 * mounted at the root of `apps/app`. Two implementations would let the admin
 * preview a banner the app never shows.
 *
 * The clock is always passed in rather than read here. A Convex query re-runs
 * when the DATABASE changes, not when time passes, so the server hands over the
 * whole window and the client re-evaluates it on a ticker.
 */

export const BANNER_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type BannerSeverity = (typeof BANNER_SEVERITIES)[number];

/**
 * Curated prefixes the admin can target. A free-text field would let a typo
 * silently target nothing, and the labels shown next to each checkbox are
 * translated per entry — both need a closed list.
 *
 * `[]` on a banner means "all pages", which is the common case (a maintenance
 * window is rarely page-specific) and therefore the default in the form.
 */
export const BANNER_TARGETS = [
  '/dashboard',
  '/bills',
  '/quotes',
  '/recurring-invoices',
  '/templates',
  '/clients',
  '/expenses',
  '/accounting/bank',
  '/employees',
  '/payslips',
  '/mandates',
  '/company',
  '/account',
  '/support',
  '/sign-in',
  '/sign-up',
  '/onboarding',
  '/welcome',
  '/portal',
] as const;

export type BannerTarget = (typeof BANNER_TARGETS)[number];

/** Mirrors `localizedTextValidator`: `fr` required, the rest optional. */
export interface LocalizedText {
  fr: string;
  de?: string;
  en?: string;
  it?: string;
}

/**
 * The banner fields this module reads. Deliberately narrower than the Convex
 * document so the app can pass a query result straight in.
 */
export interface BannerWindow {
  severity: BannerSeverity;
  startsAt: number;
  endsAt: number;
  targets: string[];
  enabled: boolean;
}

/**
 * Prefix match on path SEGMENTS: `/bills` covers `/bills` and
 * `/bills/2026-001/edit`, never `/billsomething`. A raw `startsWith` would
 * match the latter, and `/company` would then also swallow a future
 * `/company-settings`.
 *
 * No targets at all = every page.
 */
export function matchesBannerPath(
  pathname: string,
  targets: readonly string[],
): boolean {
  if (targets.length === 0) return true;
  return targets.some(
    (target) => pathname === target || pathname.startsWith(`${target}/`),
  );
}

/**
 * Inside the window and not killed. Both bounds are inclusive — the window is
 * authored to the minute, and excluding an edge would make a banner set to end
 * "at 14:00" vanish a millisecond early for no reason anyone could observe.
 */
export function isBannerActive(banner: BannerWindow, now: number): boolean {
  return banner.enabled && banner.startsAt <= now && now <= banner.endsAt;
}

export type BannerStatus = 'disabled' | 'scheduled' | 'active' | 'expired';

/**
 * Status chip for the admin table. `disabled` wins over the dates: the toggle
 * is the kill switch, and a banner pulled mid-window must not still read
 * "active".
 */
export function bannerStatus(banner: BannerWindow, now: number): BannerStatus {
  if (!banner.enabled) return 'disabled';
  if (now < banner.startsAt) return 'scheduled';
  if (now > banner.endsAt) return 'expired';
  return 'active';
}

/** critical first — the ordering the stack is rendered in. */
const SEVERITY_RANK: Record<BannerSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

/**
 * The banners to show, in the order to stack them: most severe on top, and
 * within one severity the one that started first (it has been up longest, so
 * moving it as newer ones appear would be disorienting).
 */
export function selectVisibleBanners<T extends BannerWindow>(
  banners: readonly T[],
  pathname: string,
  now: number,
): T[] {
  return banners
    .filter(
      (banner) =>
        isBannerActive(banner, now) &&
        matchesBannerPath(pathname, banner.targets),
    )
    .sort(
      (a, b) =>
        SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
        a.startsAt - b.startsAt,
    );
}

/**
 * The message in the reader's language, falling back to French — mirrors
 * i18n/config.ts FALLBACK_LANG. An empty string counts as absent: the form
 * sends the untouched optional fields as `''` rather than pruning them, and
 * rendering a blank bar would be worse than rendering the French text.
 *
 * `lang` may carry a region (`de-CH`); only the base tag is looked up.
 */
export function bannerText(message: LocalizedText, lang: string): string {
  const base = lang.split('-')[0] as keyof LocalizedText;
  const translated = message[base];
  return translated && translated.trim() !== '' ? translated : message.fr;
}
